import React, { useEffect, useState, useRef } from 'react';
import { Plus, Download, Search, Upload, FileSpreadsheet, LogIn, LogOut, ScrollText, Check, AlertCircle, X } from 'lucide-react';
import { Contributor, Solution, AvailabilityMap, AvailabilityStatus, Region, REGIONS, ExperienceLevel, ExperienceLevelMap, EXPERIENCE_LABELS } from '../types';
import { AvailabilityGrid } from './AvailabilityGrid';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';
import { useAdobeImsAuth } from '../auth/useAdobeImsAuth';
import { logAvailabilityChange, logExperienceLevelChange, upsertContributorRecord, deleteContributorRecord, reorderContributorRecords, upsertSolutionRecord, deleteSolutionRecord, reorderSolutionRecords, fetchInitialStateFromDatabase, fetchCurrentWeekSchedule, fetchNextWeekSchedule, updateDailyAvailability, fetchAuditLog } from '../utils/database';
import { ContributorWeekView } from './ContributorWeekView';
import { normalizeHydratedSolutions, reindexSolutions } from '../utils/solutionOrder';
import { normalizeHydratedContributors, reindexContributors } from '../utils/contributorOrder';
import { isLongLeaveRange, calendarDaysInclusive } from '../utils/longLeave';
function stableSlugId(input: string) {
  const normalized = input.trim().toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const slug = normalized.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (slug) {
    return slug;
  }
  let hash = 0;
  for (const char of normalized || input.trim().toLowerCase()) {
    hash = hash * 31 + char.codePointAt(0)! >>> 0;
  }
  return `id-${hash.toString(16)}`;
}
const STATUS_LABELS: Record<AvailabilityStatus, string> = {
  available: 'Qualified and Available',
  limited: 'Qualified not Available',
  unavailable: 'Not Qualified'
};
const STATUS_COLORS: Record<AvailabilityStatus, {
  bg: string;
  text: string;
  border: string;
}> = {
  available: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-300'
  },
  limited: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-300'
  },
  unavailable: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-300'
  }
};
export function Dashboard() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    accessLevel,
    canManageData,
    error: authError,
    isLoading: isAuthLoading,
    isReady: isAuthReady,
    isSignedIn,
    signIn,
    signOut,
    user
  } = useAdobeImsAuth();
  const isAdmin = accessLevel === 'superadmin' || accessLevel === 'admin';
  const hasDashboardDataAccess = isSignedIn && accessLevel !== 'unregistered' && accessLevel !== 'anonymous';
  // Filter state
  const [selectedContributorId, setSelectedContributorId] = useState<string | null>(null);
  const [selectedSolutionId, setSelectedSolutionId] = useState<string | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<AvailabilityStatus | null>(null);
  const [selectedExperienceFilter, setSelectedExperienceFilter] = useState<ExperienceLevel | null>(null);
  const [selectedRegionFilter, setSelectedRegionFilter] = useState<Region | null>(null);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [availability, setAvailability] = useState<AvailabilityMap>({});
  const [experienceLevels, setExperienceLevels] = useState<ExperienceLevelMap>({});
  const [weekViewContributorId, setWeekViewContributorId] = useState<string | null>(null); // full-grid weekly view (name click)
  const [weekPanelContributorId, setWeekPanelContributorId] = useState<string | null>(null); // slide-over panel (icon click)
  const [isHydrating, setIsHydrating] = useState(true);
  const [dbFeedback, setDbFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const dbFeedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notifyDbResult = (result: {
    success: boolean;
    error?: string;
  }) => {
    if (dbFeedbackTimer.current) clearTimeout(dbFeedbackTimer.current);
    if (result.success) {
      setDbFeedback({
        type: 'success',
        message: 'Saved'
      });
      dbFeedbackTimer.current = setTimeout(() => setDbFeedback(null), 2000);
    } else {
      setDbFeedback({
        type: 'error',
        message: 'Save failed — please try again'
      });
      dbFeedbackTimer.current = setTimeout(() => setDbFeedback(null), 5000);
    }
  };
  useEffect(() => {
    let isCancelled = false;
    if (!isAuthReady) {
      setIsHydrating(true);
      return () => {
        isCancelled = true;
      };
    }
    if (!isSignedIn) {
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    if (isSignedIn && !hasDashboardDataAccess) {
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    const loadInitialState = async () => {
      setIsHydrating(true);
      const databaseState = await fetchInitialStateFromDatabase();
      if (isCancelled) {
        return;
      }

      // Security: never fall back to sessionStorage or buildInitialState() when db-get-state
      // fails or returns 403. Client-spoofed accessLevel could otherwise load demo/cached data
      // without a successful server-authorized read.
      if (!databaseState) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
        return;
      }

      // Normalize OOO flags against today's date so stale isOutOfOffice values are corrected.
      const todayStr = new Date().toISOString().split('T')[0];
      const rawContributors = normalizeHydratedContributors(databaseState.contributors);
      const normalizedContributors = rawContributors.map(c => {
        if (c.oooStartDate && c.oooEndDate) {
          const shouldBeOOO = c.oooStartDate <= todayStr && c.oooEndDate >= todayStr;
          if (c.isOutOfOffice !== shouldBeOOO) return {
            ...c,
            isOutOfOffice: shouldBeOOO
          };
        }
        return c;
      });
      setContributors(normalizedContributors);
      setSolutions(normalizeHydratedSolutions(databaseState.solutions));
      setAvailability(databaseState.availability);
      setExperienceLevels(databaseState.experienceLevels);
      setIsHydrating(false);
    };
    loadInitialState();
    return () => {
      isCancelled = true;
    };
  }, [isAuthReady, isSignedIn, accessLevel]);

  // Modal States
  const [isAddICOpen, setIsAddICOpen] = useState(false);
  const [isAddSolutionOpen, setIsAddSolutionOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isEditContributorOpen, setIsEditContributorOpen] = useState(false);
  const [isEditSolutionOpen, setIsEditSolutionOpen] = useState(false);
  const [newICName, setNewICName] = useState('');
  const [newICEmail, setNewICEmail] = useState('');
  const [newICRegion, setNewICRegion] = useState<Region>('USA');
  const [newICNameError, setNewICNameError] = useState('');
  const [newICEmailError, setNewICEmailError] = useState('');
  const [newSolutionName, setNewSolutionName] = useState('');
  const [newSolutionNameError, setNewSolutionNameError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [importError, setImportError] = useState<string>('');
  const [editingContributor, setEditingContributor] = useState<Contributor | null>(null);
  const [editingSolution, setEditingSolution] = useState<Solution | null>(null);
  const [editContributorName, setEditContributorName] = useState('');
  const [editContributorRegion, setEditContributorRegion] = useState<Region>('USA');
  const [editSolutionName, setEditSolutionName] = useState('');
  // OOO modal state
  const [isOOOModalOpen, setIsOOOModalOpen] = useState(false);
  const [oooTargetContributorId, setOOOTargetContributorId] = useState<string | null>(null);
  const [oooStartDate, setOOOStartDate] = useState('');
  const [oooEndDate, setOOOEndDate] = useState('');
  const [oooError, setOOOError] = useState('');
  const requestManagementAccess = () => {
    if (!canManageData && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return canManageData;
  };
  const requestAdminAccess = () => {
    if (!isAdmin && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return isAdmin;
  };
  const handleAuthButtonClick = () => {
    if (isSignedIn) {
      void signOut();
      return;
    }
    void signIn();
  };
  useEffect(() => {
    if (canManageData) {
      if (isAdmin) {
        return;
      }
      setIsAddICOpen(false);
      setIsAddSolutionOpen(false);
      setIsImportOpen(false);
      return;
    }
    setIsAddICOpen(false);
    setIsAddSolutionOpen(false);
    setIsImportOpen(false);
    setIsEditContributorOpen(false);
    setIsEditSolutionOpen(false);
    setIsOOOModalOpen(false);
  }, [canManageData, isAdmin]);
  // Download current data as CSV (for editing and re-importing)
  const handleDownloadCurrentData = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const rows = contributors.map(contributor => {
      const cells = solutions.map(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const level = experienceLevels[contributor.id]?.[solution.id];
        return level ? `${status}:${level}` : status;
      });
      return [contributor.name, contributor.region, ...cells].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    link.download = `availability-data-${date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const [isExportingLog, setIsExportingLog] = useState(false);
  const handleDownloadAuditLog = async () => {
    if (!requestAdminAccess()) return;
    setIsExportingLog(true);
    try {
      const entries = await fetchAuditLog();
      if (!entries || entries.length === 0) {
        alert('No audit log entries found.');
        return;
      }

      // Collect all unique keys across all entries for CSV columns
      const keySet = new Set<string>();
      for (const entry of entries) {
        for (const key of Object.keys(entry)) keySet.add(key);
      }
      // Preferred column order; any remaining keys are appended
      const orderedKeys = ['_id', 'timestamp', 'actionType', 'actorId', 'contributorId', 'contributorName', 'region', 'solutionId', 'solutionName', 'status', 'startDate', 'endDate', 'notes'];
      const extraKeys = [...keySet].filter(k => !orderedKeys.includes(k)).sort();
      const columns = [...orderedKeys.filter(k => keySet.has(k)), ...extraKeys];
      const escape = (v: any) => {
        if (v === null || v === undefined) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csvRows = [columns.join(','), ...entries.map(e => columns.map(k => escape(e[k])).join(','))];
      const blob = new Blob([csvRows.join('\n')], {
        type: 'text/csv'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExportingLog(false);
    }
  };

  // Download template CSV
  const handleDownloadTemplate = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const exampleRow = ['John Doe', 'USA', ...solutions.map((_, i) => {
      // Show varied examples
      if (i === 0) return 'available:L3';
      if (i === 1) return 'limited:L2';
      if (i === 2) return 'unavailable';
      return 'available:L1';
    })];
    const csvContent = [headers.join(','), exampleRow.join(','), ['Jane Smith', 'EMEA', ...solutions.map(() => '')].join(',')].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'availability-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };
  // Parse and import CSV
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!requestAdminAccess()) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          setImportError('File must contain at least a header row and one data row');
          return;
        }
        // Parse header
        const headers = lines[0].split(',').map(h => h.trim());
        const contributorIndex = headers.findIndex(h => h.toLowerCase() === 'contributor');
        const regionIndex = headers.findIndex(h => h.toLowerCase() === 'region');
        if (contributorIndex === -1 || regionIndex === -1) {
          setImportError('CSV must have "Contributor" and "Region" columns');
          return;
        }
        const solutionHeaders = headers.slice(2); // Everything after Contributor and Region
        // Create solutions from headers
        const newSolutions: Solution[] = reindexSolutions(solutionHeaders.map(name => ({
          // id: crypto.randomUUID(),
          id: stableSlugId(name.trim()),
          name: name.trim()
        })));
        // Parse data rows
        const newContributors: Contributor[] = [];
        const newAvailability: AvailabilityMap = {};
        const newExperienceLevels: ExperienceLevelMap = {};
        const validLevels = ['L1', 'L2', 'L3', 'L4'];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const name = values[contributorIndex];
          const region = values[regionIndex] as Region;
          if (!name || !REGIONS.includes(region)) continue;
          // const contributorId = crypto.randomUUID();
          const contributorId = stableSlugId(name);
          newContributors.push({
            id: contributorId,
            name,
            email: '',
            region
          });
          // Parse availability and experience level for each solution
          // Format: "status" or "status:level" (e.g., "available:L2")
          newAvailability[contributorId] = {};
          newExperienceLevels[contributorId] = {};
          solutionHeaders.forEach((_, idx) => {
            const cellValue = values[idx + 2]?.trim() || '';
            const solutionId = newSolutions[idx].id;
            const parts = cellValue.split(':').map(p => p.trim());
            const statusPart = parts[0]?.toLowerCase();
            const levelPart = parts[1]?.toUpperCase();
            // Parse status
            if (statusPart === 'available' || statusPart === 'limited' || statusPart === 'unavailable') {
              newAvailability[contributorId][solutionId] = statusPart as AvailabilityStatus;
            } else {
              newAvailability[contributorId][solutionId] = 'unavailable';
            }
            // Parse experience level
            if (levelPart && validLevels.includes(levelPart)) {
              newExperienceLevels[contributorId][solutionId] = levelPart as ExperienceLevel;
            }
          });
        }
        // Update state
        setContributors(newContributors);
        setSolutions(newSolutions);
        setAvailability(newAvailability);
        setExperienceLevels(newExperienceLevels);
        setImportError('');
        setIsImportOpen(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setImportError('Failed to parse CSV file. Please check the format.');
        console.error('CSV parse error:', error);
      }
    };
    reader.readAsText(file);
  };
  // Helper to update lastUpdated timestamp for a contributor
  const touchContributor = (contributorId: string) => {
    setContributors(prev => prev.map(c => c.id === contributorId ? {
      ...c,
      lastUpdated: new Date().toISOString()
    } : c));
  };
  const saveContributorRecord = (contributor: Contributor) => {
    upsertContributorRecord(contributor).then(notifyDbResult);
  };
  const saveSolutionOrder = (orderedSolutions: Solution[]) => {
    if (orderedSolutions.length === 0) return;
    reorderSolutionRecords(orderedSolutions.map(solution => ({
      id: solution.id,
      displayOrder: solution.displayOrder
    }))).then(notifyDbResult);
  };
  const saveContributorOrder = (orderedContributors: Contributor[]) => {
    if (orderedContributors.length === 0) return;
    reorderContributorRecords(orderedContributors.map(contributor => ({
      id: contributor.id,
      displayOrder: contributor.displayOrder
    }))).then(notifyDbResult);
  };
  // Handlers
  const handleToggleAvailability = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);

    // The grid displays and edits base availability only (weekly overrides are
    // managed in the week view and never surface here). Cycle from base.
    const currentStatus = availability[contributorId]?.[solutionId] ?? 'unavailable';
    const statusCycle: Record<AvailabilityStatus, AvailabilityStatus> = {
      unavailable: 'limited',
      limited: 'available',
      available: 'unavailable'
    };
    const newStatus = statusCycle[currentStatus];
    setAvailability(prev => ({
      ...prev,
      [contributorId]: {
        ...(prev[contributorId] ?? {}),
        [solutionId]: newStatus
      }
    }));
    if (contributor && solution) {
      logAvailabilityChange(contributor, solution, newStatus, experienceLevels[contributorId]?.[solutionId]).then(notifyDbResult);
    }
    touchContributor(contributorId);
  };
  const handleCycleExperienceLevel = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const contributorLevels = experienceLevels[contributorId] || {};
    const currentLevel = contributorLevels[solutionId];
    const cycle: Record<string, ExperienceLevel | undefined> = {
      undefined: 'L1',
      L1: 'L2',
      L2: 'L3',
      L3: 'L4',
      L4: undefined as any
    };
    const newLevel = cycle[String(currentLevel)];
    setExperienceLevels(prev => {
      const nextContributorLevels = {
        ...(prev[contributorId] || {})
      };
      if (newLevel) {
        nextContributorLevels[solutionId] = newLevel;
      } else {
        delete nextContributorLevels[solutionId];
      }
      return {
        ...prev,
        [contributorId]: nextContributorLevels
      };
    });
    if (contributor && solution) {
      const currentStatus = availability[contributorId]?.[solutionId] || 'unavailable';
      logExperienceLevelChange(contributor, solution, newLevel, currentStatus).then(notifyDbResult);
    }
    touchContributor(contributorId);
  };
  const handleUpdateContributorRegion = (id: string, region: Region) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === id);
    const updatedContributor = contributor ? {
      ...contributor,
      region,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === id ? {
      ...c,
      region,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
  };
  const handleAddContributor = async () => {
    if (!requestAdminAccess()) return;
    let hasError = false;
    if (!newICName.trim()) {
      setNewICNameError('Name is required.');
      hasError = true;
    } else {
      setNewICNameError('');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newICEmail.trim()) {
      setNewICEmailError('Email is required.');
      hasError = true;
    } else if (!emailRegex.test(newICEmail.trim())) {
      setNewICEmailError('Enter a valid email address.');
      hasError = true;
    } else {
      setNewICEmailError('');
    }
    if (hasError) return;
    const contributorName = newICName.trim();
    const contributorId = stableSlugId(contributorName);
    if (!contributorId) {
      setNewICNameError('Unable to generate a valid ID for that name.');
      return;
    }
    if (contributors.some(contributor => contributor.id === contributorId)) {
      setNewICNameError('A contributor with that name already exists.');
      return;
    }
    const newContributor: Contributor = {
      // id: crypto.randomUUID(),
      id: contributorId,
      name: contributorName,
      email: newICEmail.trim(),
      region: newICRegion,
      lastUpdated: new Date().toISOString()
    };
    const nextContributors = reindexContributors([...contributors, newContributor]);
    const createdContributor = nextContributors.find(c => c.id === newContributor.id) || nextContributors[nextContributors.length - 1];
    const response = await upsertContributorRecord(createdContributor);
    notifyDbResult(response);
    if (!response.success) return;
    setContributors(nextContributors);
    setNewICName('');
    setNewICEmail('');
    setNewICRegion('USA');
    setNewICNameError('');
    setNewICEmailError('');
    setIsAddICOpen(false);
  };
  const handleAddSolution = () => {
    if (!requestAdminAccess()) return;
    if (!newSolutionName.trim()) {
      setNewSolutionNameError('Solution name is required.');
      return;
    }
    const solutionName = newSolutionName.trim();
    if (solutions.some(s => s.name.toLowerCase() === solutionName.toLowerCase())) {
      setNewSolutionNameError('A solution with that name already exists.');
      return;
    }
    setNewSolutionNameError('');
    const newSolution: Solution = {
      // id: crypto.randomUUID(),
      id: stableSlugId(solutionName),
      name: solutionName
    };
    const nextSolutions = reindexSolutions([...solutions, newSolution]);
    const createdSolution = nextSolutions.find(solution => solution.id === newSolution.id) || nextSolutions[nextSolutions.length - 1];
    setSolutions(nextSolutions);
    if (createdSolution) {
      upsertSolutionRecord(createdSolution).then(notifyDbResult);
    }
    setNewSolutionName('');
    setNewSolutionNameError('');
    setIsAddSolutionOpen(false);
  };
  const handleDeleteContributor = async (id: string) => {
    if (!requestAdminAccess()) return;
    if (confirm('Are you sure you want to remove this contributor?')) {
      const contributor = contributors.find(c => c.id === id);
      const nextContributors = reindexContributors(contributors.filter(c => c.id !== id));
      setContributors(nextContributors);
      setAvailability(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      setExperienceLevels(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      if (contributor) {
        const result = await deleteContributorRecord({
          id: contributor.id,
          name: contributor.name,
          region: contributor.region
        });
        notifyDbResult(result);
      }
      saveContributorOrder(nextContributors);
    }
  };
  const handleDeleteSolution = async (id: string) => {
    if (!requestAdminAccess()) return;
    if (confirm('Are you sure you want to remove this solution?')) {
      const solution = solutions.find(s => s.id === id);
      const nextSolutions = reindexSolutions(solutions.filter(s => s.id !== id));
      setSolutions(nextSolutions);
      setAvailability(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      setExperienceLevels(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      if (solution) {
        const result = await deleteSolutionRecord({
          id: solution.id,
          name: solution.name
        });
        notifyDbResult(result);
      }
      saveSolutionOrder(nextSolutions);
    }
  };
  // Status filter handler
  const handleStatusFilterClick = (status: AvailabilityStatus) => {
    setSelectedStatusFilter(selectedStatusFilter === status ? null : status);
  };
  // Experience level filter handler
  const handleExperienceFilterClick = (level: ExperienceLevel) => {
    setSelectedExperienceFilter(selectedExperienceFilter === level ? null : level);
  };
  // Region filter handler
  const handleRegionFilterClick = (region: Region) => {
    setSelectedRegionFilter(selectedRegionFilter === region ? null : region);
  };
  // Filter handlers
  const handleContributorClick = (contributorId: string) => {
    setWeekViewContributorId(weekViewContributorId === contributorId ? null : contributorId);
  };
  const handleCloseWeekView = () => {
    setWeekViewContributorId(null);
  };
  const handleSolutionClick = (solutionId: string) => {
    setSelectedSolutionId(selectedSolutionId === solutionId ? null : solutionId);
  };
  const handleClearFilters = () => {
    setSelectedContributorId(null);
    setSelectedSolutionId(null);
    setSelectedStatusFilter(null);
    setSelectedExperienceFilter(null);
    setSelectedRegionFilter(null);
  };
  // Filtered views - apply search AND click filters
  const filteredContributors = contributors.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredSolutions = solutions.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const hasContributorMatch = filteredContributors.length > 0;
  const hasSolutionMatch = filteredSolutions.length > 0;
  let displayContributors = searchQuery === '' ? contributors : hasContributorMatch ? filteredContributors : contributors;
  let displaySolutions = searchQuery === '' ? solutions : hasSolutionMatch ? filteredSolutions : solutions;
  // Apply click filters
  if (selectedContributorId) {
    displayContributors = displayContributors.filter(c => c.id === selectedContributorId);
  }
  if (selectedSolutionId) {
    displaySolutions = displaySolutions.filter(s => s.id === selectedSolutionId);
  }
  // Apply region filter
  if (selectedRegionFilter) {
    displayContributors = displayContributors.filter(c => c.region === selectedRegionFilter);
  }
  // Apply status + experience filters - supports multi-select (both can be active)
  let statusFilteredContributors = displayContributors;
  let statusFilteredSolutions = displaySolutions;
  if (selectedStatusFilter || selectedExperienceFilter) {
    const contributorIdsMatching = new Set<string>();
    const solutionIdsMatching = new Set<string>();
    displayContributors.forEach(contributor => {
      displaySolutions.forEach(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const expLevel = experienceLevels[contributor.id]?.[solution.id];
        const statusMatch = !selectedStatusFilter || status === selectedStatusFilter;
        const expMatch = !selectedExperienceFilter || expLevel === selectedExperienceFilter;
        if (statusMatch && expMatch) {
          contributorIdsMatching.add(contributor.id);
          solutionIdsMatching.add(solution.id);
        }
      });
    });
    statusFilteredContributors = displayContributors.filter(c => contributorIdsMatching.has(c.id));
    statusFilteredSolutions = displaySolutions.filter(s => solutionIdsMatching.has(s.id));
  }
  const selectedContributor = contributors.find(c => c.id === selectedContributorId);
  const selectedSolution = solutions.find(s => s.id === selectedSolutionId);
  const hasActiveFilters = selectedContributorId !== null || selectedSolutionId !== null || selectedStatusFilter !== null || selectedExperienceFilter !== null || selectedRegionFilter !== null;
  // Reorder handlers
  const handleReorderContributors = (fromIndex: number, toIndex: number) => {
    if (!requestAdminAccess()) return;
    const newContributors = [...contributors];
    const [removed] = newContributors.splice(fromIndex, 1);
    newContributors.splice(toIndex, 0, removed);
    const reorderedContributors = reindexContributors(newContributors);
    setContributors(reorderedContributors);
    saveContributorOrder(reorderedContributors);
  };
  const handleReorderSolutions = (fromIndex: number, toIndex: number) => {
    if (!requestAdminAccess()) return;
    const newSolutions = [...solutions];
    const [removed] = newSolutions.splice(fromIndex, 1);
    newSolutions.splice(toIndex, 0, removed);
    const reorderedSolutions = reindexSolutions(newSolutions);
    setSolutions(reorderedSolutions);
    saveSolutionOrder(reorderedSolutions);
  };
  // Edit handlers
  const handleEditContributor = (contributor: Contributor) => {
    if (!requestAdminAccess()) return;
    setEditingContributor(contributor);
    setEditContributorName(contributor.name);
    setEditContributorRegion(contributor.region);
    setIsEditContributorOpen(true);
  };
  const handleSaveContributor = () => {
    if (!requestAdminAccess()) return;
    if (!editingContributor || !editContributorName.trim()) return;
    const updatedContributor: Contributor = {
      ...editingContributor,
      name: editContributorName.trim(),
      region: editContributorRegion,
      lastUpdated: new Date().toISOString()
    };
    setContributors(prev => prev.map(c => c.id === editingContributor.id ? updatedContributor : c));
    saveContributorRecord(updatedContributor);
    setIsEditContributorOpen(false);
    setEditingContributor(null);
    setEditContributorName('');
  };
  const handleEditSolution = (solution: Solution) => {
    if (!requestAdminAccess()) return;
    setEditingSolution(solution);
    setEditSolutionName(solution.name);
    setIsEditSolutionOpen(true);
  };
  const handleSaveSolution = () => {
    if (!requestAdminAccess()) return;
    if (!editingSolution || !editSolutionName.trim()) return;
    const nextSolutions = reindexSolutions(solutions.map(s => s.id === editingSolution.id ? {
      ...s,
      name: editSolutionName.trim()
    } : s));
    const updatedSolution = nextSolutions.find(s => s.id === editingSolution.id);
    setSolutions(nextSolutions);
    if (updatedSolution) {
      upsertSolutionRecord(updatedSolution).then(notifyDbResult);
    }
    setIsEditSolutionOpen(false);
    setEditingSolution(null);
    setEditSolutionName('');
  };
  // OOO handler - now opens modal instead of toggling
  const handleOpenOOOModal = (contributorId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    setOOOTargetContributorId(contributorId);
    setOOOStartDate(contributor?.oooStartDate || '');
    setOOOEndDate(contributor?.oooEndDate || '');
    setOOOError('');
    setIsOOOModalOpen(true);
  };
  const handleSaveOOO = () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    setOOOError('');
    const contributor = contributors.find(c => c.id === oooTargetContributorId);
    // If both dates are empty, clear OOO
    if (!oooStartDate && !oooEndDate) {
      const updatedContributor = contributor ? {
        ...contributor,
        isOutOfOffice: false,
        oooStartDate: undefined,
        oooEndDate: undefined,
        lastUpdated: new Date().toISOString()
      } : null;
      setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
        ...c,
        isOutOfOffice: false,
        oooStartDate: undefined,
        oooEndDate: undefined,
        lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
      } : c));
      if (updatedContributor) {
        saveContributorRecord(updatedContributor);
      }
      setIsOOOModalOpen(false);
      return;
    }
    if (!oooStartDate || !oooEndDate) {
      setOOOError('Please select both start and end dates');
      return;
    }
    const start = new Date(oooStartDate);
    const end = new Date(oooEndDate);
    if (end < start) {
      setOOOError('End date must be on or after start date');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const isCurrentlyOOO = oooStartDate <= today && oooEndDate >= today;
    const updatedContributor = contributor ? {
      ...contributor,
      isOutOfOffice: isCurrentlyOOO,
      oooStartDate,
      oooEndDate,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
      ...c,
      isOutOfOffice: isCurrentlyOOO,
      oooStartDate,
      oooEndDate,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
    setIsOOOModalOpen(false);
  };
  const handleClearOOO = () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    const contributor = contributors.find(c => c.id === oooTargetContributorId);
    const updatedContributor = contributor ? {
      ...contributor,
      isOutOfOffice: false,
      oooStartDate: undefined,
      oooEndDate: undefined,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
      ...c,
      isOutOfOffice: false,
      oooStartDate: undefined,
      oooEndDate: undefined,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
    setIsOOOModalOpen(false);
  };
  // Date Availability Handlers
  // Icon click → slide-over panel
  const handleOpenWeekView = (contributorId: string) => {
    setWeekPanelContributorId(contributorId);
  };
  const handleClosePanelView = () => {
    setWeekPanelContributorId(null);
  };
  const handleUpdateDailyAvailability = async (contributorId: string, solutionId: string, date: string, status: AvailabilityStatus | null) => {
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const result = await updateDailyAvailability(contributorId, solutionId, date, status, contributor, solution);
    notifyDbResult(result);
  };
  const accessLevelLabel = accessLevel === 'superadmin' ? 'Superadmin' : accessLevel === 'admin' ? 'Admin' : accessLevel === 'editor' ? 'Editor' : accessLevel === 'viewer' ? 'Viewer' : accessLevel === 'unregistered' ? 'Unregistered' : 'Signed out';
  if (isHydrating) {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500 font-sans" data-id="element-140" data-mp="1pseoycccgfho">
        {isAuthReady ? 'Loading resource data...' : 'Initializing Adobe sign-in...'}
      </div>;
  }
  if (!isSignedIn) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-141" data-mp="1b4a3j0lwppv7">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-142" data-mp="1738efclpeu6y">
          <div className="flex items-center gap-4" data-id="element-143" data-mp="1kumy7lnfqzf2">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-144" data-mp="1kveja2jeper3" />
            <div data-id="element-145" data-mp="sbfyozhzze4p">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-146" data-mp="nezak5asszrm">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-147" data-mp="2txixkp0abqw">
                Sign in with Adobe ID
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-148" data-mp="6waoo93r69no">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-149" data-mp="btat6fkyf4co">
              Dashboard data is available only after Adobe IMS login.
              <br data-id="element-150" data-mp="ls00xfj4z8f7" /> <br data-id="element-151" data-mp="ls9u1bzh9vp4" />
              <p data-id="element-152" data-mp="lqp0npowgisu">
              1. Click Sign-In <br data-id="element-153" data-mp="lri005lj460y" />
              2. Login using your company account <br data-id="element-154" data-mp="psk0sqv3gq51" />
              3. Choose profile : Adobe Inc
              </p>
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-155" data-mp="uklk2wqojh4k">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="primary" size="lg" isLoading={isAuthLoading} data-id="element-156" data-mp="14irhp25ge13m">
              
              <LogIn className="mr-2 h-4 w-4" data-id="element-157" data-mp="2tzd9x39f5xmh" />
              Sign in with Adobe ID
            </Button>
          </div>
        </div>
      </div>;
  }
  if (accessLevel === 'unregistered') {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-158" data-mp="2k0xjz3m4ouek">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-159" data-mp="2f2w38pc6d769">
          <div className="flex items-center gap-4" data-id="element-160" data-mp="3cq4k2718ds1x">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-161" data-mp="3crxtfu38aqdm" />
            <div data-id="element-162" data-mp="2y0ymeni1k9w5">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-163" data-mp="2y1i3y33s4w9n">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-164" data-mp="3vh4cji7xpjzy">
                Account not provisioned
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-165" data-mp="1py9s72bletuz">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-166" data-mp="1pwyf2bw9c9r7">
              You are signed in with Adobe ID, but this account is not registered for dashboard access.
              No resource data is shown until an administrator assigns you a role in the system.
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-167" data-mp="177s1f3abm8c9">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="outline" size="lg" isLoading={isAuthLoading} data-id="element-168" data-mp="127dvbqsa58gv">
              
              <LogOut className="mr-2 h-4 w-4" data-id="element-169" data-mp="sbswu2mf6qbv" />
              Sign out
            </Button>
          </div>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-screen bg-slate-50 text-slate-900 font-sans" data-id="element-170" data-mp="2moirwnoiy5ku">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 shadow-sm z-30" data-id="element-171" data-mp="2v5j7r6c5sexj">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-172" data-mp="2v3q5024o2eut">
          <div className="flex items-center gap-3" data-id="element-173" data-mp="2z4rx7z061qna">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-12 w-auto" data-id="element-174" data-mp="33yw6wq0tgcs2" />
            
            <div data-id="element-175" data-mp="2c8hwx4vxbklo">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-176" data-mp="2c91g0oxb8f1a">
                Resource Availability Dashboard
              </h1>
              <div className="flex items-center gap-2" data-id="element-177" data-mp="2l6ty20uf20sd">
                <p className="text-sm text-slate-500" data-id="element-178" data-mp="2l9g41w1ns88h">
                  Track solution coverage and individual capacity
                </p>
                {dbFeedback && <div className={`flex items-center gap-1 text-xs animate-in fade-in duration-200 ${dbFeedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-179" data-mp="gcpkijoerc1">
                    {dbFeedback.type === 'success' ? <Check className="h-3 w-3" data-id="element-180" data-mp="36ywc4f9qele4" /> : <AlertCircle className="h-3 w-3" data-id="element-181" data-mp="370h6a3s4w2bg" />}
                    <span data-id="element-182" data-mp="3lvx66pfeasfr">{dbFeedback.message}</span>
                  </div>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap" data-id="element-183" data-mp="3guhisqpqw9u7">
            <div className="relative hidden md:block" data-id="element-184" data-mp="2t819vltqn2ji">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-185" data-mp="2o7wu5y0nmbuc" />
              <input type="text" placeholder="Search ICs or solutions..." className="pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] w-64" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-186" data-mp="32wsz3m7ez8qq" />
              
            </div>
            <div className="h-8 w-px bg-slate-200 mx-2 hidden md:block" data-id="element-187" data-mp="25fks7cdwxckq" />

            {isSignedIn && <div className="hidden lg:flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600" data-id="element-188" data-mp="kxjnqb80i79f">
                <span className={`h-2 w-2 rounded-full ${canManageData ? 'bg-green-500' : 'bg-amber-500'}`} data-id="element-189" data-mp="b2887qos4w0m" />
                <span className="font-medium text-slate-800" data-id="element-190" data-mp="g0jk06ymxglh">
                  {user?.displayName || 'Adobe user'}
                </span>
                {user?.email && <span className="text-slate-500" data-id="element-191" data-mp="18nw50cp5dgco">{user.email}</span>}
                <span className={`rounded-full px-2 py-0.5 font-medium ${accessLevel === 'superadmin' || accessLevel === 'admin' ? 'bg-slate-900 text-white' : accessLevel === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-192" data-mp="upxtdf27ny0v">
                  
                  {accessLevelLabel}
                </span>
              </div>}

            <Button onClick={handleAuthButtonClick} variant={isSignedIn ? 'outline' : 'primary'} size="sm" isLoading={isAuthLoading && !isSignedIn} data-id="element-193" data-mp="3iqyccy1x0nl">
              
              {isSignedIn ? <LogOut className="h-4 w-4 mr-2" data-id="element-194" data-mp="3pz3epbmp2bvq" /> : <LogIn className="h-4 w-4 mr-2" data-id="element-195" data-mp="3kziih8ywshmp" />}
              {isSignedIn ? 'Sign Out' : 'Sign in with Adobe ID'}
            </Button>

            {isAdmin && <>
                <Button onClick={handleDownloadCurrentData} variant="outline" size="sm" data-id="element-196" data-mp="hiifgmjv6x5t">

                  <Download className="h-4 w-4 mr-2" data-id="element-197" data-mp="cidzcxmvc4g4" />
                  Export Data
                </Button>
                <Button onClick={handleDownloadAuditLog} variant="outline" size="sm" isLoading={isExportingLog} data-id="element-198" data-mp="8h268i4sd136">

                  <ScrollText className="h-4 w-4 mr-2" data-id="element-199" data-mp="2t7t53p4hgjpq" />
                  Log
                </Button>
                {/* <Button
                 onClick={() => setIsImportOpen(true)}
                 variant="outline"
                 size="sm">
                                   <Upload className="h-4 w-4 mr-2" />
                  Import
                 </Button> */}
                <Button onClick={() => setIsAddICOpen(true)} variant="outline" size="sm" data-id="element-200" data-mp="1mhf3x7qxzn6p">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-201" data-mp="1rg010yawdqqx" />
                  Add Contributor
                </Button>
                <Button onClick={() => setIsAddSolutionOpen(true)} variant="primary" size="sm" data-id="element-202" data-mp="1wdruvxv3rmxr">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-203" data-mp="25gj4rt8byana" />
                  Add Solution
                </Button>
              </>}
          </div>
        </div>

        <div className="flex justify-end" data-id="element-204" data-mp="b29xbsnox4ht">
          <span className="text-xs hover:text-blue-500" data-id="element-205" data-mp="b3b77tsyzkxy"><a target="_blank" href="https://adobe-my.sharepoint.com/:x:/r/personal/dsagar_adobe_com/_layouts/15/doc2.aspx?sourcedoc=%7B430e4f3f-47f7-428f-9fb3-2c6f5f88ee7d%7D&action=edit" data-id="element-206" data-mp="b433l6ue5mpa">Feedback</a></span><span className="text-xs ml-1 italic" data-id="element-207" data-mp="19gfg7b0lpcyp">v2.0</span>
        </div>
        {/* Mobile Search */}
        <div className="mt-4 md:hidden relative" data-id="element-208" data-mp="1nbhjrrhz6q8c">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-209" data-mp="1idpp4ppy4q0g" />
          <input type="text" placeholder="Search ICs or solutions..." className="w-full pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000]" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-210" data-mp="1xb00lz2l9vw6" />
          
        </div>

        {(authError || isAuthReady && !isSignedIn || isSignedIn && !canManageData || isSignedIn && canManageData && !isAdmin) && <div className="mt-4 space-y-2" data-id="element-211" data-mp="2b3yaeqfs3qj9">
            {authError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-212" data-mp="2b1lok26wgw0x">
                {authError}
              </div>}
            {isAuthReady && !isSignedIn && !authError && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-213" data-mp="2kralw47faqlh">
                Sign in with Adobe ID to unlock
              </div>}
            {isSignedIn && !canManageData && accessLevel === 'viewer' && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-id="element-214" data-mp="2bxfj32trxc90">
                Signed in as{' '}
                <span className="font-medium" data-id="element-215" data-mp="2212pe4y8aye9">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                This account has viewer access only.
              </div>}
            {isSignedIn && canManageData && !isAdmin && <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-id="element-216" data-mp="1x1gcjwmbbk5z">
                Signed in as{' '}
                <span className="font-medium" data-id="element-217" data-mp="2dfnvkgi2lpmw">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                
              </div>}
          </div>}
      </header>

      {/* Legend & Active Filters */}
      <div className="bg-white border-b border-slate-200 px-6 py-2" data-id="element-218" data-mp="1yjygl2baycro">
        <div className="flex flex-col gap-2" data-id="element-219" data-mp="1ylsq7kh4iigg">
          <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-220" data-mp="1uicwavwfuw7g">
            <div className="flex items-center gap-4 text-xs" data-id="element-221" data-mp="1pro75n88ebbh">
              <span className="font-medium text-slate-600" data-id="element-222" data-mp="1pq3c7wi08qd3">Status:</span>
              {/* Clickable status filters */}
              <button onClick={() => handleStatusFilterClick('available')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'available' ? 'bg-green-100 ring-2 ring-green-500 ring-offset-1' : 'hover:bg-green-50'}`} title="Click to filter by Qualified and Available" data-id="element-223" data-mp="1kpyr1t65j7gj">
                
                <div className="h-3 w-3 rounded-full bg-green-500" data-id="element-224" data-mp="3ii88vnjnedw6"></div>
                <span className={selectedStatusFilter === 'available' ? 'text-green-700 font-medium' : 'text-slate-700'} data-id="element-225" data-mp="3mfwblcrl1aym">
                  
                  Qualified and Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('limited')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'limited' ? 'bg-yellow-100 ring-2 ring-yellow-500 ring-offset-1' : 'hover:bg-yellow-50'}`} title="Click to filter by Qualified not Available" data-id="element-226" data-mp="2yq1x6qrm7ec9">
                
                <div className="h-3 w-3 rounded-full bg-yellow-400" data-id="element-227" data-mp="2fzarq0ar0835"></div>
                <span className={selectedStatusFilter === 'limited' ? 'text-yellow-700 font-medium' : 'text-slate-700'} data-id="element-228" data-mp="2kyx4k8mnzmbf">
                  
                  Qualified not Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('unavailable')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'unavailable' ? 'bg-red-100 ring-2 ring-red-500 ring-offset-1' : 'hover:bg-red-50'}`} title="Click to filter by Not Qualified" data-id="element-229" data-mp="1lrz2gu50hoj6">
                
                <div className="h-3 w-3 rounded-full bg-red-500" data-id="element-230" data-mp="123htwi84zv3q"></div>
                <span className={selectedStatusFilter === 'unavailable' ? 'text-red-700 font-medium' : 'text-slate-700'} data-id="element-231" data-mp="1721kivxodv2f">
                  
                  Not Qualified
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-232" data-mp="efb98jlmeq1s" />

              <span className="font-medium text-slate-600" data-id="element-233" data-mp="9foycgt9llw5">Level:</span>
              {/* Clickable experience level filters */}
              <button onClick={() => handleExperienceFilterClick('L1')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L1' ? 'bg-slate-200 ring-2 ring-slate-400 ring-offset-1' : 'hover:bg-slate-50'}`} title="Click to filter by L1 - Foundational" data-id="element-234" data-mp="ng9et6bpj5mh">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200" data-id="element-235" data-mp="1725sjd6pbjdo">
                  L1
                </span>
                <span className={selectedExperienceFilter === 'L1' ? 'text-slate-800 font-medium' : 'text-slate-700'} data-id="element-236" data-mp="1g3cc5v4ndab5">
                  
                  Foundational
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-237" data-mp="dqh7bvsmubz0">
                  FOUNDATIONAL

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-238" data-mp="mkvs2dvxmepi">Enablement Requirements:</p>
                  <p data-id="element-239" data-mp="rjystm4vpyro">Intro-level enablement completed with understanding of the basics and value of the solution.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-240" data-mp="11g20t8mewrmj">Tooling Expectations:</p>
                  <p data-id="element-241" data-mp="15hn9sivtslrg">Learners can do a standard demo in a clickable and shared environment.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-242" data-mp="337etmzb3v928">Field Support Implications:</p>
                  <p data-id="element-243" data-mp="3790f391pgvo1">Learners are apprenticing in the field and can support 1:Many demos.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-244" data-mp="37atkym4hvbuz">Outcome:</p>
                  <p data-id="element-245" data-mp="2zrkd4xbmzl81">Learners should be able to deliver a value pitch and 101 level demo.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L2')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L2' ? 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1' : 'hover:bg-blue-50'}`} title="Click to filter by L2 - Intermediate" data-id="element-246" data-mp="2pzejlj5qkvqq">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-blue-50 text-blue-600 border-blue-200" data-id="element-247" data-mp="39nf36di6wiku">
                  L2
                </span>
                <span className={selectedExperienceFilter === 'L2' ? 'text-blue-700 font-medium' : 'text-slate-700'} data-id="element-248" data-mp="34qokrjiwxaw9">
                  
                  Intermediate
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-249" data-mp="3h4sbvh2elt1a">
                  INTERMEDIATE

                  <p className="font-bold mt-2" data-id="element-250" data-mp="3c7tln9lv268d">Enablement Requirements:</p>
                  <p data-id="element-251" data-mp="3qyq6f7e7o4b">Deeper dives into features, customer use Requests, and common integrations.</p>

                  <p className="font-bold mt-2" data-id="element-252" data-mp="8qjruxdy6akq">Tooling Expectations:</p>
                  <p data-id="element-253" data-mp="3qvwnq7ws8xrw">Learners can do an ad hoc demo in a live environment.</p>

                  <p className="font-bold mt-2" data-id="element-254" data-mp="3qy0vj0xsygxg">Field Support Implications:</p>
                  <p data-id="element-255" data-mp="3vtg71m8c80qp">Learners are supporting early-stage or low complexity single solution customer meetings.</p>

                  <p className="font-bold mt-2" data-id="element-256" data-mp="2fiu9vkm2imbf">Outcome:</p>
                  <p data-id="element-257" data-mp="2kgvqzzzxobk9">Learners have completed numerous customer demos that show practical application. They should be doing ad hoc demos where they’re able to go off script.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L3')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L3' ? 'bg-indigo-100 ring-2 ring-indigo-400 ring-offset-1' : 'hover:bg-indigo-50'}`} title="Click to filter by L3 - Advanced" data-id="element-258" data-mp="27wp0zym2fqvt">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-indigo-50 text-indigo-600 border-indigo-200" data-id="element-259" data-mp="1oau5g1uup55z">
                  L3
                </span>
                <span className={selectedExperienceFilter === 'L3' ? 'text-indigo-700 font-medium' : 'text-slate-700'} data-id="element-260" data-mp="1y6dxgd6anjpg">
                  
                  Advanced
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-261" data-mp="35lj0vrehmzui">
                  ADVANCED

                  <p className="font-bold mt-2" data-id="element-262" data-mp="3jceaj5wt9kz5">Enablement Requirements:</p>
                  <p data-id="element-263" data-mp="3jeid9kiz4dxu">Covers integrations with other solutions, data migration architectures, and expert-level guidance.</p>

                  <p className="font-bold mt-2" data-id="element-264" data-mp="2rl89mn99nj8t">Tooling Expectations:</p>
                  <p data-id="element-265" data-mp="2rnc8urw7zu2r">Learners can demo product integrations and add-ons in a live environment.</p>

                  <p className="font-bold mt-2" data-id="element-266" data-mp="2voxtizezxcni">Field Support Implications:</p>
                  <p data-id="element-267" data-mp="30nhh1498mwhz">Learners are supporting all single solution customer meetings.</p>

                  <p className="font-bold mt-2" data-id="element-268" data-mp="avko9w459kkb">Outcome:</p>
                  <p data-id="element-269" data-mp="fvfbnpopns0m">Learners can easily lead ad hoc demos and go off script live with a customer. Learners have a comprehensive understanding of the tool and multiple closed deals to show practical application.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L4')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L4' ? 'bg-violet-100 ring-2 ring-violet-400 ring-offset-1' : 'hover:bg-violet-50'}`} title="Click to filter by L4 - Champion" data-id="element-270" data-mp="l1b1fcgnk5tp">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200" data-id="element-271" data-mp="28qai0kikasj">
                  L4
                </span>
                <span className={selectedExperienceFilter === 'L4' ? 'text-violet-700 font-medium' : 'text-slate-700'} data-id="element-272" data-mp="3pfsotbswidxt">
                  
                  Champion
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-273" data-mp="26vmb7bsw3s8b">
                  CHAMPION

                  <p className="font-bold mt-2" data-id="element-274" data-mp="225fip0v9bnks">Field Support Implications:</p>
                  <p data-id="element-275" data-mp="223baw7u8m4f8">Learners can support architecture and multi-solution conversations. The most complex meetings.</p>

                  <p className="font-bold mt-2" data-id="element-276" data-mp="1s6ztx4jpcqeg">Outcome:</p>
                  <p data-id="element-277" data-mp="1s8jbcyh3h7ht">Learners are the SME on the team for an individual product and a thought leader as the product evolves.</p>
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-278" data-mp="1j7l2o2vpobsw" />

              <span className="font-medium text-slate-600" data-id="element-279" data-mp="38lhudoc8tgff">Region:</span>
              {REGIONS.map(region => {
              const isActive = selectedRegionFilter === region;
              const colorMap: Record<Region, {
                active: string;
                hover: string;
                text: string;
                dot: string;
              }> = {
                USA: {
                  active: 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1',
                  hover: 'hover:bg-blue-50',
                  text: 'text-blue-700 font-medium',
                  dot: 'bg-blue-500'
                },
                LATAM: {
                  active: 'bg-amber-100 ring-2 ring-amber-400 ring-offset-1',
                  hover: 'hover:bg-amber-50',
                  text: 'text-amber-700 font-medium',
                  dot: 'bg-amber-500'
                },
                India: {
                  active: 'bg-emerald-100 ring-2 ring-emerald-400 ring-offset-1',
                  hover: 'hover:bg-emerald-50',
                  text: 'text-emerald-700 font-medium',
                  dot: 'bg-emerald-500'
                },
                EMEA: {
                  active: 'bg-purple-100 ring-2 ring-purple-400 ring-offset-1',
                  hover: 'hover:bg-purple-50',
                  text: 'text-purple-700 font-medium',
                  dot: 'bg-purple-500'
                }
              };
              const colors = colorMap[region];
              return <button key={region} onClick={() => handleRegionFilterClick(region)} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${isActive ? colors.active : colors.hover}`} title={`Click to filter by ${region}`} data-id="element-280" data-mp="15hcjw342kom7">
                    
                    <div className={`h-2.5 w-2.5 rounded-sm ${colors.dot}`} data-id="element-281" data-mp="2pu1yrb3lq3v7" />
                    <span className={isActive ? colors.text : 'text-slate-700'} data-id="element-282" data-mp="2lu1db6i34sen">
                      {region}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && <div className="flex items-center gap-2" data-id="element-283" data-mp="2jeuwcyze1lv9">
              <span className="text-xs font-medium text-slate-600" data-id="element-284" data-mp="2jgeaaixknktv">
                Filtered:
              </span>
              {selectedStatusFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${STATUS_COLORS[selectedStatusFilter].bg} ${STATUS_COLORS[selectedStatusFilter].text}`} data-id="element-285" data-mp="3c3se90zotgnh">
              
                  {STATUS_LABELS[selectedStatusFilter]}
                  <button onClick={() => setSelectedStatusFilter(null)} className={`hover:${STATUS_COLORS[selectedStatusFilter].bg} rounded-full p-0.5`} data-id="element-286" data-mp="32bmmnsdcl18t">
                
                    <X className="h-3 w-3" data-id="element-287" data-mp="3v0a2v9j8p0fi" />
                  </button>
                </span>}
              {selectedExperienceFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedExperienceFilter === 'L1' ? 'bg-slate-100 text-slate-700' : selectedExperienceFilter === 'L2' ? 'bg-blue-100 text-blue-700' : selectedExperienceFilter === 'L3' ? 'bg-indigo-100 text-indigo-700' : 'bg-violet-100 text-violet-700'}`} data-id="element-288" data-mp="djxpx57mg5fh">
              
                  {selectedExperienceFilter} –{' '}
                  {EXPERIENCE_LABELS[selectedExperienceFilter]}
                  <button onClick={() => setSelectedExperienceFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-289" data-mp="3lzr2xa2quyrs">
                
                    <X className="h-3 w-3" data-id="element-290" data-mp="3gy1qpr3scf24" />
                  </button>
                </span>}
              {selectedRegionFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedRegionFilter === 'USA' ? 'bg-blue-100 text-blue-700' : selectedRegionFilter === 'LATAM' ? 'bg-amber-100 text-amber-700' : selectedRegionFilter === 'India' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`} data-id="element-291" data-mp="37zzlezd0r091">
              
                  {selectedRegionFilter}
                  <button onClick={() => setSelectedRegionFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-292" data-mp="1sgigj1emx58k">
                
                    <X className="h-3 w-3" data-id="element-293" data-mp="zscumbbuha75" />
                  </button>
                </span>}
              {(selectedStatusFilter || selectedExperienceFilter || selectedRegionFilter) && [selectedStatusFilter, selectedExperienceFilter, selectedRegionFilter].filter(Boolean).length > 1 && <span className="text-[10px] text-slate-400 italic" data-id="element-294" data-mp="1dlcaiefjh6f8">
                    (combined filters)
                  </span>}
              {selectedContributor && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs" data-id="element-295" data-mp="l1drhfts3duy">
                  {selectedContributor.name}
                  <button onClick={() => setSelectedContributorId(null)} className="hover:bg-blue-200 rounded-full p-0.5" data-id="element-296" data-mp="3t5xu0c65w63j">
                
                    <X className="h-3 w-3" data-id="element-297" data-mp="6xy732yhc208" />
                  </button>
                </span>}
              {selectedSolution && <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs" data-id="element-298" data-mp="knrvxl9zim9y">
                  {selectedSolution.name}
                  <button onClick={() => setSelectedSolutionId(null)} className="hover:bg-purple-200 rounded-full p-0.5" data-id="element-299" data-mp="unskpmqmhulj">
                
                    <X className="h-3 w-3" data-id="element-300" data-mp="pmbldvnseg72" />
                  </button>
                </span>}
              <button onClick={handleClearFilters} className="text-xs text-slate-500 hover:text-slate-700 underline" data-id="element-301" data-mp="2wggopyb3cm2d">
              
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden p-4 md:p-6" data-id="element-302" data-mp="1byo4ja3n7oca">
        {weekViewContributorId ? (() => {
        const weekContributor = contributors.find(c => c.id === weekViewContributorId);
        if (!weekContributor) return null;
        return <div className="h-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col" data-id="element-303" data-mp="o67jcqolzg8r">
              <ContributorWeekView contributor={weekContributor} solutions={solutions} baseAvailability={availability} canEdit={canManageData} onClose={handleCloseWeekView} fetchCurrentWeek={fetchCurrentWeekSchedule} fetchNextWeek={fetchNextWeekSchedule} onUpdateDailyAvailability={(sId, date, status) => handleUpdateDailyAvailability(weekContributor.id, sId, date, status)} showTabs={false} data-id="element-304" data-mp="s9x578dwfjq3" />
            </div>;
      })() : <AvailabilityGrid contributors={statusFilteredContributors} solutions={statusFilteredSolutions} availability={availability} onToggleAvailability={handleToggleAvailability} onDeleteContributor={handleDeleteContributor} onDeleteSolution={handleDeleteSolution} onUpdateContributorRegion={handleUpdateContributorRegion} onContributorClick={handleContributorClick} onSolutionClick={handleSolutionClick} selectedContributorId={selectedContributorId} selectedSolutionId={selectedSolutionId} selectedStatusFilter={selectedStatusFilter} selectedExperienceFilter={selectedExperienceFilter} onReorderContributors={handleReorderContributors} onReorderSolutions={handleReorderSolutions} isAdminAuthenticated={canManageData} canAdministerEntities={isAdmin} canDeleteEntities={isAdmin} allContributors={contributors} allSolutions={solutions} onEditContributor={handleEditContributor} onEditSolution={handleEditSolution} onToggleOutOfOffice={handleOpenOOOModal} experienceLevels={experienceLevels} onCycleExperienceLevel={handleCycleExperienceLevel} onOpenWeekView={hasDashboardDataAccess ? handleOpenWeekView : undefined} data-id="element-305" data-mp="277rkj35vkfd5" />}
      </main>

      {/* Weekly schedule slide-over panel (icon click) */}
      {weekPanelContributorId && (() => {
      const panelContributor = contributors.find(c => c.id === weekPanelContributorId);
      if (!panelContributor) return null;
      return <div className="fixed inset-y-0 right-0 z-40 flex" data-id="element-306" data-mp="rmqqep3uwyyr">
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={handleClosePanelView} data-id="element-307" data-mp="rn8u26sumfgp" />
            <div className="relative ml-auto w-full max-w-3xl bg-white shadow-xl flex flex-col border-l border-slate-200" data-id="element-308" data-mp="17yutjs7rkgyl">
              <ContributorWeekView contributor={panelContributor} solutions={solutions} baseAvailability={availability} canEdit={canManageData} onClose={handleClosePanelView} fetchCurrentWeek={fetchCurrentWeekSchedule} fetchNextWeek={fetchNextWeekSchedule} onUpdateDailyAvailability={(sId, date, status) => handleUpdateDailyAvailability(panelContributor.id, sId, date, status)} data-id="element-309" data-mp="1lym4fk2zhh8d" />
            </div>
          </div>;
    })()}

      {/* Import Modal */}
      <Modal isOpen={isImportOpen} onClose={() => {
      setIsImportOpen(false);
      setImportError('');
    }} title="Import Availability Data" footer={<div className="flex justify-between items-center w-full" data-id="element-311" data-mp="2h6o2qptp2nij">
            <div className="flex gap-2" data-id="element-312" data-mp="2lypgkg6igmao">
              <Button variant="outline" onClick={handleDownloadTemplate} size="sm" data-id="element-313" data-mp="2lzh3l2m1lbpc">
              
                <FileSpreadsheet className="h-4 w-4 mr-2" data-id="element-314" data-mp="2d5m9cpmgl5oh" />
                Blank Template
              </Button>
              <Button variant="outline" onClick={handleDownloadCurrentData} size="sm" data-id="element-315" data-mp="30ux0dfe0blpf">
              
                <Download className="h-4 w-4 mr-2" data-id="element-316" data-mp="34w8v1zbqmx4h" />
                Current Data
              </Button>
            </div>
            <Button variant="ghost" onClick={() => setIsImportOpen(false)} data-id="element-317" data-mp="2qzmxyqjp5cb4">
              Close
            </Button>
          </div>} data-id="element-310" data-mp="ahw24c1ol9k1">
        
        <div className="space-y-4" data-id="element-318" data-mp="1fh2i4p9gcesq">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-319" data-mp="1fk6rs25os2qs">
            <h4 className="font-medium text-blue-900 text-sm mb-2" data-id="element-320" data-mp="1kirqfx5aied4">
              CSV Format Instructions
            </h4>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside" data-id="element-321" data-mp="1t05a47yqmi2v">
              <li data-id="element-322" data-mp="1xvas4vp0rclo">
                First column: <strong data-id="element-323" data-mp="22w8gxr4jb4w5">Contributor</strong> (name)
              </li>
              <li data-id="element-324" data-mp="22x06olbga8ei">
                Second column: <strong data-id="element-325" data-mp="27v1l2sxxljjn">Region</strong> (USA, LATAM, India, or
                EMEA)
              </li>
              <li data-id="element-326" data-mp="1a7t3h911wumx">Remaining columns: Solution names</li>
              <li data-id="element-327" data-mp="1a9dzl330klmw">
                Cell format:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-328" data-mp="1f5uow0ly961k">status</code> or{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-329" data-mp="1k60ktsz95epg">status:level</code>
              </li>
              <li data-id="element-330" data-mp="1owh1dlyddgg1">
                Status values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-331" data-mp="1oy0dd0czt5c0">available</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-332" data-mp="rg0fxu4r41ml">limited</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-333" data-mp="wfmnpo1t8po0">unavailable</code>
              </li>
              <li data-id="element-334" data-mp="39qxxo96om3re">
                Level values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-335" data-mp="39pmr61luqhwl">L1</code>{' '}
                (Foundational),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-336" data-mp="35mzxdv5memwr">L2</code>{' '}
                (Intermediate),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-337" data-mp="2vqn5n2tmyj4n">L3</code> (Advanced),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-338" data-mp="2vsr6fbw8n20p">L4</code> (Champion)
              </li>
              <li data-id="element-339" data-mp="2qtoduqxg2o9m">
                Examples:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-340" data-mp="2m0ldnzy5wgkg">available:L3</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-341" data-mp="2hyqfv5ffwlbf">limited:L1</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-342" data-mp="2d05h7afu69p3">unavailable</code>
              </li>
              <li data-id="element-343" data-mp="2812kqedx9bsq">
                Leave cells empty to default to "unavailable" with no level
              </li>
            </ul>
          </div>

          {importError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-id="element-344" data-mp="232r9q0bw4v8x">
              {importError}
            </div>}

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-slate-400 transition-colors" data-id="element-345" data-mp="1ydq990ma2bqi">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className="hidden" id="csv-upload" data-id="element-346" data-mp="1kf9ucme9hexa" />
            
            <label htmlFor="csv-upload" className="cursor-pointer" data-id="element-347" data-mp="2c77hhp9ik218">
              <Upload className="h-12 w-12 mx-auto text-slate-400 mb-3" data-id="element-348" data-mp="2h3g3nmm4ovhw" />
              <p className="text-sm font-medium text-slate-700 mb-1" data-id="element-349" data-mp="23bhzzrb6rrt4">
                Click to upload CSV file
              </p>
              <p className="text-xs text-slate-500" data-id="element-350" data-mp="27cbnwkomp6m0">or drag and drop</p>
            </label>
          </div>

          <p className="text-xs text-slate-500" data-id="element-351" data-mp="2v2fo0i2lyo89">
            <strong data-id="element-352" data-mp="2zuz0tcth1fc0">Note:</strong> Importing will replace all current data.
            Download your current data first if you want to keep it.
          </p>
        </div>
      </Modal>

      {/* Add Contributor Modal */}
      <Modal isOpen={isAddICOpen} onClose={() => {
      setIsAddICOpen(false);
      setNewICNameError('');
      setNewICEmailError('');
    }} title="Add New Contributor" footer={<div className="flex justify-end gap-3" data-id="element-354" data-mp="t789axywydsl">
            <Button variant="ghost" onClick={() => setIsAddICOpen(false)} data-id="element-355" data-mp="t8jdv006npkr">
              Cancel
            </Button>
            <Button onClick={handleAddContributor} data-id="element-356" data-mp="hrj86yrn8zj">Add Contributor</Button>
          </div>} data-id="element-353" data-mp="jfm01764yosh">
        
        <div className="space-y-4" data-id="element-357" data-mp="ffbjx1omqhzw">
          <p className="text-sm text-slate-500" data-id="element-358" data-mp="fgd7g5mu0wyi">
            Enter the name and region of the individual contributor you would
            like to add to the dashboard.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={newICName} onChange={e => {
          setNewICName(e.target.value);
          setNewICNameError('');
        }} error={newICNameError} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-359" data-mp="1qsw6hl371056" />

          <Input label="Email" placeholder="e.g. jane.doe@adobe.com" type="email" value={newICEmail} onChange={e => {
          setNewICEmail(e.target.value);
          setNewICEmailError('');
        }} error={newICEmailError} onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-360" data-mp="exyy621wx4y" />

          <div data-id="element-361" data-mp="jao0js62nrib">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-362" data-mp="jaeicqr9qon6">
              Region
            </label>
            <select value={newICRegion} onChange={e => setNewICRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-363" data-mp="o37n3emqlwz9">
              
              {REGIONS.map(region => <option key={region} value={region} data-id="element-364" data-mp="1c75kc2b6np2e">
                  {region}
                </option>)}
            </select>
          </div>
        </div>
      </Modal>

      {/* Add Solution Modal */}
      <Modal isOpen={isAddSolutionOpen} onClose={() => {
      setIsAddSolutionOpen(false);
      setNewSolutionNameError('');
    }} title="Add New Solution" footer={<div className="flex justify-end gap-3" data-id="element-366" data-mp="1uvkcisx8l94s">
            <Button variant="ghost" onClick={() => setIsAddSolutionOpen(false)} data-id="element-367" data-mp="1pxitu93q3btu">
              Cancel
            </Button>
            <Button onClick={handleAddSolution} data-id="element-368" data-mp="1l6aih7iu1ge1">Add Solution</Button>
          </div>} data-id="element-365" data-mp="128gej7wk9v2i">
        
        <div className="space-y-4" data-id="element-369" data-mp="1h5qoudxgwuwn">
          <p className="text-sm text-slate-500" data-id="element-370" data-mp="1h4pao0l5nsap">
            Enter the name of the new Adobe solution or product area.
          </p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={newSolutionName} onChange={e => {
          setNewSolutionName(e.target.value);
          setNewSolutionNameError('');
        }} error={newSolutionNameError} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddSolution()} data-id="element-371" data-mp="2doxxq6b5e177" />
          
        </div>
      </Modal>

      {/* Edit Contributor Modal */}
      <Modal isOpen={isEditContributorOpen} onClose={() => {
      setIsEditContributorOpen(false);
      setEditingContributor(null);
      setEditContributorName('');
    }} title="Edit Contributor" footer={<div className="flex justify-end gap-3" data-id="element-373" data-mp="3afl22fcno6mz">
            <Button variant="ghost" onClick={() => {
        setIsEditContributorOpen(false);
        setEditingContributor(null);
        setEditContributorName('');
      }} data-id="element-374" data-mp="3ah4etvz3rzk0">
            
              Cancel
            </Button>
            <Button onClick={handleSaveContributor} data-id="element-375" data-mp="1dm8ax1pfo0ig">Save Changes</Button>
          </div>} data-id="element-372" data-mp="272y34vl8ll4">
        
        <div className="space-y-4" data-id="element-376" data-mp="2y63pem52hdj2">
          <p className="text-sm text-slate-500" data-id="element-377" data-mp="2y391ulqrbclf">
            Update the contributor's name and region.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={editContributorName} onChange={e => setEditContributorName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveContributor()} data-id="element-378" data-mp="37v54ma3sfqvz" />
          
          <div data-id="element-379" data-mp="3cui0fkcn08ap">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-380" data-mp="3cuzvidnkcgh1">
              Region
            </label>
            <select value={editContributorRegion} onChange={e => setEditContributorRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-381" data-mp="3qsogrmljlpmm">
              
              {REGIONS.map(region => <option key={region} value={region} data-id="element-382" data-mp="15n6htzugw5wy">
                  {region}
                </option>)}
            </select>
          </div>
        </div>
      </Modal>

      {/* Edit Solution Modal */}
      <Modal isOpen={isEditSolutionOpen} onClose={() => {
      setIsEditSolutionOpen(false);
      setEditingSolution(null);
      setEditSolutionName('');
    }} title="Edit Solution" footer={<div className="flex justify-end gap-3" data-id="element-384" data-mp="rn57zuw85o5c">
            <Button variant="ghost" onClick={() => {
        setIsEditSolutionOpen(false);
        setEditingSolution(null);
        setEditSolutionName('');
      }} data-id="element-385" data-mp="rq16plfwcguu">
            
              Cancel
            </Button>
            <Button onClick={handleSaveSolution} data-id="element-386" data-mp="281exwtg47kya">Save Changes</Button>
          </div>} data-id="element-383" data-mp="1ffm1r2pbr4kh">
        
        <div className="space-y-4" data-id="element-387" data-mp="2czynr4xtxgvx">
          <p className="text-sm text-slate-500" data-id="element-388" data-mp="2d34bafqt8apj">Update the solution name.</p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={editSolutionName} onChange={e => setEditSolutionName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveSolution()} data-id="element-389" data-mp="1d4rz7v48d5l" />
          
        </div>
      </Modal>

      {/* OOO Date Range Modal */}
      <Modal isOpen={isOOOModalOpen} onClose={() => setIsOOOModalOpen(false)} title="Set Out of Office" footer={<div className="flex justify-between w-full" data-id="element-391" data-mp="39w3wszq94mhb">
            {oooStartDate || oooEndDate ? <Button variant="danger" size="sm" onClick={handleClearOOO} data-id="element-392" data-mp="34whkcsi8za9k">
                Clear OOO
              </Button> : <div data-id="element-393" data-mp="133tfyftvags7" />}
            <div className="flex gap-3" data-id="element-394" data-mp="131yu8zarukn2">
              <Button variant="ghost" onClick={() => setIsOOOModalOpen(false)} data-id="element-395" data-mp="130xggn2dfk1n">
                Cancel
              </Button>
              <Button onClick={handleSaveOOO} data-id="element-396" data-mp="2zgkg8zb95fkh">Save</Button>
            </div>
          </div>} data-id="element-390" data-mp="3dw4dkr0tp9rj">
        
        <div className="space-y-4" data-id="element-397" data-mp="2up2dc5po54wv">
          {(() => {
          const contributor = contributors.find(c => c.id === oooTargetContributorId);
          return contributor ? <div className="bg-slate-50 p-3 rounded-md border border-slate-200" data-id="element-398" data-mp="2lpp155a5u28g">
                <p className="text-sm text-slate-600" data-id="element-399" data-mp="3iferhybibxvw">
                  Setting Out of Office for{' '}
                  <span className="font-semibold text-slate-900" data-id="element-400" data-mp="3ed1r7pfj4s6i">
                    {contributor.name}
                  </span>
                </p>
              </div> : null;
        })()}

          <p className="text-sm text-slate-500" data-id="element-401" data-mp="34fwzzb6tu6iu">
            Select a date range for when you'll be out of office. Your entire
            column will appear dimmed during this period.
          </p>

          <div className="grid grid-cols-2 gap-4" data-id="element-402" data-mp="1szp41slwhtg5">
            <Input type="date" label="Start Date" value={oooStartDate} onChange={e => setOOOStartDate(e.target.value)} data-id="element-403" data-mp="1t2bdjxscqj10" />
            
            <Input type="date" label="End Date" value={oooEndDate} onChange={e => setOOOEndDate(e.target.value)} min={oooStartDate || undefined} data-id="element-404" data-mp="1nt0ks6k0gmmv" />
            
          </div>

          {oooError && <p className="text-sm text-red-600" data-id="element-405" data-mp="21lhuolb8qq9u">{oooError}</p>}

          {oooStartDate && oooEndDate && (() => {
          const today = new Date().toISOString().split('T')[0];
          const isActive = oooStartDate <= today && oooEndDate >= today;
          const isFuture = oooStartDate > today;
          const isLongLeave = isLongLeaveRange(oooStartDate, oooEndDate);
          return <div className={`p-3 rounded-md border text-sm ${isActive ? 'bg-orange-50 border-orange-200 text-orange-700' : isFuture ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-id="element-406" data-mp="h8iui31lzs1h">

                  {isActive && '⚡ OOO is currently active'}
                  {isFuture && `📅 OOO scheduled: ${new Date(oooStartDate).toLocaleDateString()} – ${new Date(oooEndDate).toLocaleDateString()}`}
                  {!isActive && !isFuture && 'This date range is in the past'}
                  {isLongLeave && <div className="mt-1 font-semibold text-purple-700" data-id="element-407" data-mp="x2mjb6orkcu3">
                      ⏳ Long leave ({calendarDaysInclusive(oooStartDate, oooEndDate)} days)
                    </div>}
                </div>;
        })()}
        </div>
      </Modal>

    </div>;
}